/**
 * DECKSTR.GG — Arena File Watcher (Browser Edition)
 * ──────────────────────────────────────────────────
 * Uses the File System Access API to poll MTGA's Player.log
 * for new data. Replaces Node's fs.watch for browser use.
 *
 * Browser support: Chrome 86+, Edge 86+ (Chromium-based)
 */

export type FileWatcherStatus = 'disconnected' | 'selecting' | 'connected' | 'error';

export class ArenaFileWatcher {
    private _handle: FileSystemFileHandle | null = null;
    private _offset: number = 0;
    private _pollTimer: ReturnType<typeof setInterval> | null = null;
    private _status: FileWatcherStatus = 'disconnected';
    private _fileName: string = '';
    private _onChunk: ((text: string) => void) | null = null;
    private _onStatus: ((status: FileWatcherStatus, msg?: string) => void) | null = null;
    private _pollMs: number;

    constructor({
        pollMs = 250,
        onStatus,
    }: {
        pollMs?: number;
        onStatus?: (status: FileWatcherStatus, msg?: string) => void;
    } = {}) {
        this._pollMs = pollMs;
        this._onStatus = onStatus ?? null;
    }

    get status(): FileWatcherStatus {
        return this._status;
    }

    get fileName(): string {
        return this._fileName;
    }

    get isConnected(): boolean {
        return this._status === 'connected';
    }

    /**
     * Check if the File System Access API is available.
     */
    static isSupported(): boolean {
        return typeof window !== 'undefined' && 'showOpenFilePicker' in window;
    }

    /**
     * Open a file picker and let the user select their Player.log.
     * Returns true if a file was selected successfully.
     */
    async selectLogFile(): Promise<boolean> {
        if (!ArenaFileWatcher.isSupported()) {
            this._setStatus('error', 'File System Access API not supported. Use Chrome or Edge.');
            return false;
        }

        this._setStatus('selecting');

        try {
            const [handle] = await (window as any).showOpenFilePicker({
                types: [{
                    description: 'MTGA Player Log',
                    accept: { 'text/plain': ['.log'] },
                }],
                multiple: false,
            });

            this._handle = handle;
            this._fileName = handle.name;

            // Read the tail of the file (last ~50KB) to catch any current game state
            const file = await handle.getFile();
            const TAIL_BYTES = 50 * 1024; // 50KB lookback
            this._offset = Math.max(0, file.size - TAIL_BYTES);

            this._setStatus('connected', `Selected ${handle.name} (${(file.size / 1024).toFixed(0)}KB, reading last ${Math.min(TAIL_BYTES, file.size)} bytes)`);
            return true;
        } catch (err: any) {
            if (err.name === 'AbortError') {
                // User cancelled the file picker
                this._setStatus('disconnected');
                return false;
            }
            this._setStatus('error', `Failed to open file: ${err.message}`);
            return false;
        }
    }

    /**
     * Start polling for new data. Calls onChunk with new text.
     */
    start(onChunk: (text: string) => void): void {
        if (!this._handle) {
            this._setStatus('error', 'No file selected. Call selectLogFile() first.');
            return;
        }

        this._onChunk = onChunk;
        this.stop(); // Clear any existing timer

        this._pollTimer = setInterval(() => this._poll(), this._pollMs);
    }

    /**
     * Stop polling.
     */
    stop(): void {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    /**
     * Disconnect and reset state.
     */
    disconnect(): void {
        this.stop();
        this._handle = null;
        this._offset = 0;
        this._fileName = '';
        this._onChunk = null;
        this._setStatus('disconnected');
    }

    /**
     * Poll the file for new data since last read.
     */
    private async _poll(): Promise<void> {
        if (!this._handle || !this._onChunk) return;

        try {
            const file = await this._handle.getFile();

            // No new data
            if (file.size <= this._offset) return;

            // Read only the new bytes
            const blob = file.slice(this._offset, file.size);
            const text = await blob.text();
            this._offset = file.size;

            if (text.length > 0) {
                this._onChunk(text);
            }
        } catch (err: any) {
            // File may have been rotated or deleted
            if (err.name === 'NotFoundError' || err.name === 'NotReadableError') {
                this._setStatus('error', 'Player.log not accessible. Is MTGA running?');
                this.stop();
            } else {
                // Log unexpected errors so they're not silently lost
                console.warn('[ArenaFileWatcher] poll error:', err.name, err.message);
            }
        }
    }

    private _setStatus(status: FileWatcherStatus, msg?: string): void {
        this._status = status;
        this._onStatus?.(status, msg);
    }
}
